﻿import {translation} from "@2d-front-monorepo/core";
import {LinkButton, MaterialIcon, Message, TabPanel, Tabs} from "@2d-front-monorepo/design-library";
import {GroupedPermit, InstallationByHostData, PermitResult, Permits} from "@2d-front-monorepo/permits-business";
import {PermitTranslations} from "@2d-front-monorepo/shared-translations";

import {JSX, useEffect, useMemo} from "react";
import AddCreditCard from "../../components/AddCreditCard";
import TopUserInfo from "../../components/TopUserInfo";
import {PermitsState, reloadPermits} from "../../contexts/PermitsContext";
import Registrations from "../../customers/Registrations";
import {useGlobalContext} from "../../hooks/useGlobalContext";
import {usePermits} from "../../hooks/usePermits";
import {PrivateRoutes} from "../../routes";
import ArchivedPermits from "./ArchivedPermits";
import PermitListComponent from "./ViewComponents/PermitListComponent";

type FilteredPermitsProps = PermitsState & {
	installation: InstallationByHostData;
	filtered: PermitResult[];
	topMenu: JSX.Element;
}

function filterPermitsByTariffs(permits: PermitResult[], tariffIds: string[]): PermitResult[] {
	return permits.filter(permit => tariffIds.includes(permit.tariffId));
}

function getUngroupedPermits(permits: PermitResult[]): GroupedPermit[] {
	return permits.map(x => {
		return {
			permits,
			permit: x,
			name: "",
			count: 1,
			startDate: x.startDate,
			endDate: x.endDate
		}
	});
}

function FilteredPermits(props: FilteredPermitsProps): JSX.Element {
	const {installation, permits, holderData, filtered, topMenu} = props;

	const grouped = installation.configuration.useGroupName
		? Permits.getGroupedPermits(filtered)
		: getUngroupedPermits(filtered);

	return <>
		<AddCreditCard/>
		<div className="form__buttons form__buttons--top">
			<LinkButton to={PrivateRoutes.permitsHome} small={true}>
				<MaterialIcon icon="add"/>
				{translation(PermitTranslations.summary_page__add_new_permit)}
			</LinkButton>
			{topMenu}
		</div>
		<PermitListComponent permits={permits} holderData={holderData} grouped={grouped} now={installation.now}/>
	</>;
}

type TabData = {
	id: string;
	title: string;
	content: JSX.Element;
}

export default function PermitList(): JSX.Element {
	const {permits, holderData} = usePermits();
	const {installation} = useGlobalContext();
	const TopMenuComponent = Registrations.getPermitsTopMenu();

	useEffect(() => {
		void reloadPermits()
		const interval = setInterval(reloadPermits, 20000);
		return () => clearInterval(interval);
	}, []);

	const hasPermits = permits.permits.length > 0 || permits.hasArchived;
	const isEmpty = !hasPermits && !permits.hasArchived;

	const tabs = useMemo<TabData[]>(() => {
		const result: TabData[] = [];
		for (const [key, tariffIds] of Object.entries(installation.configuration.permitGroups)) {
			const filtered = filterPermitsByTariffs(permits.permits, tariffIds);
			if (filtered.length == 0) {
				continue
			}

			result.push({
				id: key,
				title: translation(key),
				content: <FilteredPermits installation={installation} permits={permits} holderData={holderData}
				                          filtered={filtered}
				                          topMenu={<TopMenuComponent permits={permits} holderData={holderData}/>}/>
			});
		}

		if (result.length === 0) {
			result.push({
				id: "active",
				title: translation(PermitTranslations.summary_page__active),
				content: <FilteredPermits installation={installation} permits={permits} holderData={holderData}
				                          filtered={permits.permits}
				                          topMenu={<TopMenuComponent permits={permits} holderData={holderData}/>}/>
			});
		}

		if (permits.hasArchived) {
			result.push({
				id: "archived",
				title: translation(PermitTranslations.summary_page__archived),
				content: <ArchivedPermits/>
			});
		}

		return result;
	}, [permits.holder, permits.permits, permits.hasArchived]);

	return (
		<>
			<TopUserInfo/>
			<h1>{translation(PermitTranslations.summary_page__title)}</h1>
			{isEmpty && <>
                <hr/>
                <Message title={{text: translation(PermitTranslations.summary_page__no_permits)}}
                         type="information"/></>}
			{hasPermits &&
                <Tabs>
					{tabs.map(tab => (
						<TabPanel key={tab.id} title={tab.title}>
							{tab.content}
						</TabPanel>
					))}
                </Tabs>
			}
		</>
	);
}
